import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { OptionItem, SaleTypeEnum } from '@classifieds-sell/datalayer';
import { Subject } from 'rxjs';
import { FormHelperService } from '../../../helpers/form-helper.service';
import { DraftFacade } from '@classifieds-sell/state';
import { PropertySectionBase } from '../../property-section-base';
import { ListingDurationService } from '../../../services/listing-duration.service';
import { ValidatorPipe } from '@classifieds-sell/shared';

@Component({
    selector: 'property-new-homes-sale-details-section',
    templateUrl: './property-new-homes-sale-details-section.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./property-new-homes-sale-details-section.component.scss']
})
export class PropertyNewHomesSaleDetailsSectionComponent
    extends PropertySectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    @ViewChild('propertyNewHomesSaleDetailsSectionForm')
    propertyNewHomesSaleDetailsSectionForm: NgForm;

    minDate = new Date();
    durationDate: Date;

    askingPriceLabel = 'Asking price';
    enquiriesOverLabel = 'Enquiries over';
    auctionDateLabel = 'Auction date';
    tenderDateLabel = 'Tender closing date';
    deadlineDateLabel = 'Deadline date';

    typeOfSaleInputLabel = this.askingPriceLabel;
    typeOfSaleInputDescription = 'This is the price buyers will see';

    labels: Record<SaleTypeEnum, string> = {
        [SaleTypeEnum.askingPrice]: this.askingPriceLabel,
        [SaleTypeEnum.enquiriesOver]: this.enquiriesOverLabel,
        [SaleTypeEnum.auction]: this.auctionDateLabel,
        [SaleTypeEnum.tender]: this.tenderDateLabel,
        [SaleTypeEnum.deadlinePrivateTreaty]: this.deadlineDateLabel,
        [SaleTypeEnum.priceByNegotiation]: ''
    };

    values: Record<SaleTypeEnum, Date | number | null> = {
        [SaleTypeEnum.askingPrice]: null,
        [SaleTypeEnum.enquiriesOver]: null,
        [SaleTypeEnum.auction]: null,
        [SaleTypeEnum.tender]: null,
        [SaleTypeEnum.deadlinePrivateTreaty]: null,
        [SaleTypeEnum.priceByNegotiation]: null
    };

    durationOptions: OptionItem[] = [];
    hasBeenAnExtension = false;

    constructor(
        public draftFacade: DraftFacade,
        private _changeDetectorRef: ChangeDetectorRef,
        private _formHelperService: FormHelperService,
        private _validatorPipe: ValidatorPipe,
        private _durationsService: ListingDurationService
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            this.durationOptions = this._durationsService.getDurationItems(this.metadata);
            this.updateAvailableText();
            this.updateLabelsAndValues();
            this.hasBeenAnExtension = this.draftData.propertyInformation.duration === 0;
            this._changeDetectorRef.markForCheck();
        });
    }

    isValid(): boolean {
        this._formHelperService.validate(
            this.propertyNewHomesSaleDetailsSectionForm
        );
        return this.propertyNewHomesSaleDetailsSectionForm.form.valid;
    }

    dirty(): boolean {
        return this.propertyNewHomesSaleDetailsSectionForm.form.dirty;
    }

    updateAvailableText() {
        const duration = this.draftData.propertyInformation.duration;
        this.durationDate = this._durationsService.availableDateText(this.metadata, duration);
    }

    isTypeOfSalePrice(): boolean {
        return (
            this.draftData.propertyInformation.propertyPriceType ===
                SaleTypeEnum.askingPrice ||
            this.draftData.propertyInformation.propertyPriceType ===
                SaleTypeEnum.enquiriesOver
        );
    }

    isTypeOfSaleDate(): boolean {
        return (
            this.draftData.propertyInformation.propertyPriceType ===
                SaleTypeEnum.auction ||
            this.draftData.propertyInformation.propertyPriceType ===
                SaleTypeEnum.tender ||
            this.draftData.propertyInformation.propertyPriceType ===
                SaleTypeEnum.deadlinePrivateTreaty
        );
    }

    typeOfSaleChange() {
        this.draftData.propertyInformation.propertyPricePrice = null;
        this.draftData.propertyInformation.propertyPriceDate = null;

        if (this.isTypeOfSalePrice()) {
            this.draftData.propertyInformation.propertyPricePrice =
                this.values[
                    this.draftData.propertyInformation
                        .propertyPriceType as SaleTypeEnum
                ];
        }

        if (this.isTypeOfSaleDate()) {
            this.draftData.propertyInformation.propertyPriceDate =
                this.values[
                    this.draftData.propertyInformation
                        .propertyPriceType as SaleTypeEnum
                ];
        }

        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.typeOfSalePrice?.markAsUntouched();
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.typeOfSalePrice?.setErrors(
            null
        );
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.typeOfSalePrice?.updateValueAndValidity();
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.typeOfSaleDate?.markAsUntouched();
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.typeOfSaleDate?.setErrors(
            null
        );
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.typeOfSaleDate?.updateValueAndValidity();

        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.auctionTime?.updateValueAndValidity();
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.auctionTime?.markAsUntouched();
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.auctionTime?.setErrors(
            null
        );
        this.publish();
    }

    updateLabelsAndValues() {
        this.typeOfSaleInputLabel =
            this.labels[
                this.draftData.propertyInformation
                    .propertyPriceType as SaleTypeEnum
            ];

        if (this.isTypeOfSalePrice()) {
            this.values[
                this.draftData.propertyInformation
                    .propertyPriceType as SaleTypeEnum
            ] = this.draftData.propertyInformation.propertyPricePrice;
        }

        if (this.isTypeOfSaleDate()) {
            this.values[
                this.draftData.propertyInformation
                    .propertyPriceType as SaleTypeEnum
            ] = this.draftData.propertyInformation.propertyPriceDate;
        }
    }

    onTypeOfSalePriceChange(value: string): void {
        this.values[
            this.draftData.propertyInformation.propertyPriceType as SaleTypeEnum
        ] = Number(value);
    }

    onTypeOfSaleDateChange(newDate: Date): void {
        this.values[
            this.draftData.propertyInformation.propertyPriceType as SaleTypeEnum
        ] = newDate;
        this.draftData.propertyInformation.propertyPriceDate = newDate;
        this.publish();
    }

    getTypeOfSaleValueValidation(): any {
        if (this.isTypeOfSalePrice()) {
            return this._validatorPipe.transform({
                ...this.validation.propertyInformation.propertyPricePrice,
                name: this.labels[
                    this.draftData.propertyInformation
                        .propertyPriceType as SaleTypeEnum
                ]
            });
        }

        if (this.isTypeOfSaleDate()) {
            return {
                ...this.validation.propertyInformation.propertyPriceDate,
                name: this.labels[
                    this.draftData.propertyInformation
                        .propertyPriceType as SaleTypeEnum
                ]
            };
        }
    }

    isAuctionPriceType(){
        return this.draftData.propertyInformation.propertyPriceType === SaleTypeEnum.auction;
    }

    auctionTimeChange(){
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.auctionTime?.updateValueAndValidity();
        this.propertyNewHomesSaleDetailsSectionForm?.form?.controls?.auctionTime?.markAsUntouched();
        this.publish();
    }

    auctionTimeValidation(){
        return this._validatorPipe.transform({
            name: 'Auction time',
            validations: {
                required: { value: false},
                customValidation: {
                    errorMessage: 'Auction time must be in the future',
                    enable: true,
                    validate: (auctionTime: number) => {
                        const dateValue = this.draftData.propertyInformation.propertyPriceDate;
                        // if the date value is not entered, validation will not fire
                        // auctionTime 0 means users has not selected a time
                        if (!dateValue || auctionTime === 0) {
                            return true;
                        }
                        const dateComponent = new Date(dateValue);
                        const hour = Math.trunc(auctionTime);
                        const mininute = auctionTime - hour;
                        dateComponent.setHours(hour, mininute * 60);
                        return dateComponent > new Date();
                    }
                }
            }
        });
    }
}
